import { useState, useEffect } from "react";
import { Users, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCommunitiesByType, CommunityType } from "@/hooks/useCommunitiesByType";
import { supabase } from "@/integrations/supabase/client";
import { AuthModal } from "@/components/AuthModal";

interface CommunitiesTypeFilterProps {
  selectedCommunity: string;
  onCommunityChange: (value: string) => void;
}

export const CommunitiesTypeFilter = ({ 
  selectedCommunity, 
  onCommunityChange 
}: CommunitiesTypeFilterProps) => {
  const [selectedType, setSelectedType] = useState<string>("Farmers Markets");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { communitiesByType, loading } = useCommunitiesByType();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);
  
  // Get communities for the selected type
  const currentCommunities = communitiesByType[selectedType] || [];

  const communityTypes = [
    { key: "Farmers Markets", label: "Farmer's Markets" },
    { key: "Growing Style", label: "Growing Style" },
    { key: "Associations", label: "Associations" }
  ];

  console.log('🏠 CommunitiesTypeFilter - Render with:', {
    selectedCommunity,
    selectedType,
    loading,
    communitiesCount: currentCommunities.length
  });

  const handleTypeChange = (type: string) => {
    setSelectedType(type);
    onCommunityChange('all'); // Reset community selection when type changes
  };

  return (
    <Card className="filter-card rounded-xl bg-white/95 shadow-sm" style={{ borderColor: 'var(--ys-mint-200)' }}>
      <CardContent className="p-3">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4 text-gray-900" />
              <span className="font-medium text-sm md:text-base text-gray-900">Communities</span>
              {loading && (
                <div className="text-xs text-gray-700">Loading...</div>
              )}
            </div>
            <div className="text-xs md:text-sm text-gray-700">
              {currentCommunities.length} available
            </div>
          </div>
          
          {/* Community Type Buttons */}
          <div className="flex flex-wrap gap-1">
            {communityTypes.map((type) => (
              <Button
                key={type.key}
                variant={selectedType === type.key ? "default" : "outline"}
                size="sm"
                onClick={() => handleTypeChange(type.key)}
                className="text-xs h-6 px-2"
                disabled={loading}
              >
                {type.label}
              </Button>
            ))}
            <Button
              variant={selectedCommunity === 'see-all' ? "default" : "outline"}
              size="sm"
              onClick={() => onCommunityChange('see-all')}
              className="text-xs h-6 px-2 bg-white hover:bg-gray-50 font-medium"
              disabled={loading}
            >
              SEE ALL
            </Button>
          </div>
          
          {/* Community Selection */}
          <Select 
            value={selectedCommunity} 
            onValueChange={(value) => {
              console.log('🏠 Community Filter - Selecting:', value);
              onCommunityChange(value);
            }}
            disabled={loading}
          >
            <SelectTrigger className="h-8 text-xs bg-white border border-gray-200">
              <SelectValue placeholder={`All ${communityTypes.find(t => t.key === selectedType)?.label || selectedType}`} />
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-200 shadow-lg z-[1000] max-h-60 overflow-y-auto">
              <SelectItem value="all" className="text-xs">All {communityTypes.find(t => t.key === selectedType)?.label || selectedType}</SelectItem>
              {currentCommunities.map((community) => (
                <SelectItem 
                  key={community.id} 
                  value={community.name}
                  className="text-xs hover:bg-gray-100"
                >
                  {community.name}
                  {community.members_count && community.members_count > 0 && (
                    <span className="ml-2 text-xs opacity-70">({community.members_count})</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Login to Join Community Message - Only show when not authenticated */}
          {!isAuthenticated && (
            <div className="rounded-lg px-3 py-2" style={{ background: 'var(--ys-mint-50)', color: 'var(--ys-mint-700)', border: '1px solid var(--ys-mint-200)' }}>
              <p className="text-xs md:text-sm text-center">
                🌱 <button 
                  onClick={() => setShowAuthModal(true)}
                  className="font-medium underline hover:opacity-80 transition-opacity"
                >
                  Login to Join a Community
                </button> - Connect & share with local farmers
              </p>
            </div>
          )}
        </div>
      </CardContent>

      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </Card>
  );
};